import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';
import AddMoneyModal from './AddMoneyModal';

function WalletCard({ session }) {
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);

  useEffect(() => {
    fetchWalletBalance();
    fetchTransactions();  // ← ADDED: Fetch transactions on load
  }, [session]);

  const fetchWalletBalance = async () => {
    try {
      const response = await fetch(`${API_BASE}/wallet/me`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Wallet data:', data);
        setBalance(data.balance_bigint || 0);
      } else {
        console.error('Failed to fetch wallet:', response.status);
      }
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
    }
  };

  // ← NEW: Fetch transaction history
  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API_BASE}/wallet/transactions`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Transactions loaded:', data.transactions?.length || 0, 'transactions');
        setTransactions(data.transactions || []);
      } else {
        console.error('Failed to fetch transactions:', response.status);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handleRecharge = async () => {
    const amount = parseInt(rechargeAmount);

    if (!amount || amount < 10) {
      toast.error('Minimum recharge amount is ₹10');
      return;
    }

    if (amount > 100000) {
      toast.error('Maximum recharge amount is ₹1,00,000');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/payments/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          amount: amount * 100,
          currency: 'INR'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create order');
      }

      const order = await response.json();

      if (!order || !order.id || !order.amount) {
        throw new Error('Invalid order response');
      }

      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;

      if (!razorpayKey) {
        throw new Error('Razorpay key not configured');
      }

      if (typeof window.Razorpay === 'undefined') {
        throw new Error('Razorpay script not loaded');
      }

      const options = {
        key: razorpayKey,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'ZeroPing',
        description: 'Wallet Recharge',
        order_id: order.id,
        handler: async function (response) {
          console.log('✅ Payment successful:', response);

          try {
            console.log('Verifying payment...');
            const verifyResponse = await fetch(`${API_BASE}/payments/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            const verifyData = await verifyResponse.json();
            console.log('Verify response:', verifyData);

            if (verifyData.success) {
              const amountAdded = (verifyData.amountAdded / 100).toFixed(2);
              toast.success(`Recharge successful! 💰 ₹${amountAdded} added`);
              setShowRecharge(false);
              setRechargeAmount('');
              await fetchWalletBalance();
              await fetchTransactions();  // ← ADDED: Refresh transactions after payment
              setLoading(false);
            } else {
              toast.error('Payment verification failed');
              setLoading(false);
            }
          } catch (error) {
            console.error('Verification error:', error);
            toast.error('Failed to verify payment');
            setLoading(false);
          }
        },
        prefill: {
          email: session.user.email,
        },
        theme: {
          color: '#00f0ff',
        },
        modal: {
          ondismiss: function () {
            console.log('Payment cancelled by user');
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('❌ Recharge error:', error);
      toast.error('Failed to initiate recharge: ' + error.message);
      setLoading(false);
    }
  };

  const quickAmounts = [100, 500, 1000, 2000, 5000];

  return (
    <>
      <div className="wallet-card">
        <h3 style={{
          fontSize: '1.2rem',
          marginBottom: '10px',
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '2px'
        }}>
          Wallet Balance
        </h3>
        <div className="wallet-balance">
          ₹{(balance / 100).toFixed(2)}
        </div>
        <div style={{
          display: 'flex',
          gap: '10px',
          marginTop: '20px',
          position: 'relative',
          zIndex: 1
        }}>
          <button
            className="btn btn-primary"
            onClick={() => setShowRecharge(true)}
            style={{ flex: 1 }}
          >
            💳 Recharge
          </button>
          <button
            className="btn btn-outline"
            onClick={() => setShowTransactions(!showTransactions)}
            style={{ flex: 1 }}
          >
            📊 History ({transactions.length})
          </button>
        </div>
      </div>

      {/* Recharge Modal */}
      {showRecharge && (
        <AddMoneyModal
          session={session}
          onClose={() => setShowRecharge(false)}
          onSuccess={() => {
            setShowRecharge(false);
            fetchWalletBalance();
            fetchTransactions();
          }}
        />
      )}

      {/* Transaction History Modal */}
      {showTransactions && (
        <div className="modal-overlay" onClick={() => setShowTransactions(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Transaction History</h3>
              <button className="modal-close" onClick={() => setShowTransactions(false)}>
                ×
              </button>
            </div>

            <div>
              {transactions.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: 'var(--text-secondary)'
                }}>
                  <p style={{ fontSize: '3rem', marginBottom: '10px' }}>📭</p>
                  <p>No transactions yet</p>
                </div>
              ) : (
                <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id}>
                          <td>{new Date(tx.created_at).toLocaleString()}</td>
                          <td>
                            <span className={`badge badge-${tx.type === 'credit' ? 'success' : 'warning'}`}>
                              {tx.type}
                            </span>
                          </td>
                          <td style={{
                            color: tx.type === 'credit' ? 'var(--success-color)' : 'var(--danger-color)',
                            fontWeight: 'bold'
                          }}>
                            {tx.type === 'credit' ? '+' : '-'}₹{Math.abs(tx.amount_bigint / 100).toFixed(2)}
                          </td>
                          <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            {tx.reference || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default WalletCard;